import { deepEqual } from "fast-equals";

import { BulkEvent, BulkQueue } from "./bulkQueue";
import { API_BASE_URL, APP_BASE_URL, IS_SERVER, SDK_VERSION } from "./config";
import { ReflagContext, ReflagDeprecatedContext } from "./context";
import {
  AutoFeedback,
  Feedback,
  feedback,
  FeedbackOptions,
  RequestFeedbackData,
  RequestFeedbackOptions,
} from "./feedback/feedback";
import * as feedbackLib from "./feedback/ui";
import {
  CheckEvent,
  FallbackFlagOverride,
  FlagsClient,
  OptInFlag,
  RawFlags,
} from "./flag/flags";
import { isValidFlagStateVersion } from "./flag/flagStateVersion";
import { HookArgs, HooksManager, State } from "./hooksManager";
import { HttpClient } from "./httpClient";
import { Logger, loggerWithPrefix, quietConsoleLogger } from "./logger";
import {
  AblySSEChannel,
  EventSourceFactory,
  openAblySSEChannel,
  PubSubMessage,
} from "./sse";
import { StorageAdapter } from "./storage";
import { showToolbarToggle } from "./toolbar";
import { ToolbarPosition } from "./ui/types";
import { logResponseError } from "./utils/responseError";

const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
const isNode = typeof document === "undefined"; // deno supports "window" but not "document" according to https://remix.run/docs/en/main/guides/gotchas

function normalizeContext({
  otherContext,
  ...context
}: ReflagDeprecatedContext): ReflagContext {
  return {
    user: context.user?.id ? { ...context.user } : undefined,
    company: context.company?.id ? { ...context.company } : undefined,
    other: { ...otherContext, ...context.other },
  };
}

/**
 * (Internal) User context.
 *
 * @internal
 */
export type User = {
  /**
   * Identifier of the user.
   */
  userId: string;

  /**
   * User attributes.
   */
  attributes?: {
    /**
     * Name of the user.
     */
    name?: string;

    /**
     * Email of the user.
     */
    email?: string;

    /**
     * Avatar URL of the user.
     */
    avatar?: string;

    /**
     * Custom attributes of the user.
     */
    [key: string]: any;
  };

  /**
   * Custom context of the user.
   */
  context?: PayloadContext;
};

/**
 * (Internal) Company context.
 *
 * @internal
 */
export type Company = {
  /**
   * User identifier.
   */
  userId: string;

  /**
   * Company identifier.
   */
  companyId: string;

  /**
   * Company attributes.
   */
  attributes?: {
    /**
     * Name of the company.
     */
    name?: string;

    /**
     * Custom attributes of the company.
     */
    [key: string]: any;
  };

  context?: PayloadContext;
};

/**
 * Tracked event.
 */
export type TrackedEvent = {
  /**
   * Event name.
   */
  event: string;

  /**
   * User identifier.
   */
  userId: string;

  /**
   * Company identifier.
   */
  companyId?: string;

  /**
   * Event attributes.
   */
  attributes?: Record<string, any>;

  /**
   * Custom context of the event.
   */
  context?: PayloadContext;
};

/**
 * (Internal) Custom context of the event.
 *
 * @internal
 */
export type PayloadContext = {
  /**
   * Whether the company and user associated with the event are active.
   */
  active?: boolean;
};

/**
 * ReflagClient configuration.
 */
export interface Config {
  /**
   * Base URL of Reflag servers.
   */
  apiBaseUrl: string;

  /**
   * Base URL of the Reflag web app.
   */
  appBaseUrl: string;

  /**
   * Base URL used for pubsub SSE connections.
   * Defaults to `apiBaseUrl`.
   */
  sseBaseUrl: string;

  /**
   * Whether to enable tracking.
   */
  enableTracking: boolean;

  /**
   * Whether to enable offline mode.
   */
  offline: boolean;

  /**
   * Whether the client is bootstrapped.
   */
  bootstrapped: boolean;
}

/**
 * Toolbar options.
 */
export type ToolbarOptions =
  | boolean
  | {
      show?: boolean;
      position?: ToolbarPosition;
    };

/**
 * Flag definitions.
 */
export type FlagDefinitions = Readonly<Array<string>>;

/**
 * Pre-fetched evaluated state used to bootstrap the client.
 */
export type BootstrappedState = {
  context: ReflagContext;
  flags: RawFlags;
  flagStateVersion?: number;
};

/**
 * ReflagClient initialization options.
 */
export type InitOptions = ReflagDeprecatedContext & {
  /**
   * Publishable key for authentication
   */
  publishableKey: string;

  /**
   * You can provide a logger to see the logs of the network calls.
   * This is undefined by default.
   * For debugging purposes you can just set the browser console to this property:
   * ```javascript
   * options.logger = window.console;
   * ```
   */
  logger?: Logger;

  /**
   * Base URL of Reflag servers. You can override this to use your mocked server.
   */
  apiBaseUrl?: string;

  /**
   * Base URL of the Reflag web app. Links open ín this app by default.
   */
  appBaseUrl?: string;

  /**
   * Whether to enable offline mode. Defaults to `false`.
   */
  offline?: boolean;

  /**
   * Flag keys for which `isEnabled` should fallback to true
   * if SDK fails to fetch flags from Reflag servers. If a record
   * is supplied instead of array, the values of each key represent the
   * configuration values and `isEnabled` is assume `true`.
   */
  fallbackFlags?: string[] | Record<string, FallbackFlagOverride>;

  /**
   * Timeout in milliseconds when fetching flags
   */
  timeoutMs?: number;

  /**
   * If set to true stale flags will be returned while refetching flags
   */
  staleWhileRevalidate?: boolean;

  /**
   * If set, flags will be cached between page loads for this duration
   */
  expireTimeMs?: number;

  /**
   * Stale flags will be returned if staleWhileRevalidate is true if no new flags can be fetched
   */
  staleTimeMs?: number;

  /**
   * When proxying requests, you may want to include credentials like cookies
   * so you can authorize the request in the proxy.
   * This option controls the `credentials` option of the fetch API.
   */
  credentials?: "include" | "same-origin" | "omit";

  /**
   * @deprecated SSE now uses the same origin as `apiBaseUrl` by default.
   * Override only if you need a separate pubsub host temporarily.
   */
  sseBaseUrl?: string;

  /**
   * AutoFeedback specific configuration
   */
  feedback?: FeedbackOptions;

  /**
   * Version of the SDK
   */
  sdkVersion?: string;

  /**
   * Whether to enable tracking. Defaults to `true`.
   */
  enableTracking?: boolean;

  /**
   * Whether to enable live flag updates.
   *
   * When enabled, the SDK opens a Server-Sent Events (SSE) connection and
   * refreshes flag definitions automatically whenever they change on the
   * server, without relying on context changes or manual refreshes.
   *
   * Defaults to `false` in the browser SDK.
   */
  enableLiveFlagUpdates?: boolean;

  /**
   * Optional factory used to create SSE connections.
   *
   * By default the SDK uses the global `EventSource` implementation available
   * in browsers. This option is intended for alternative runtimes where you
   * need to provide an EventSource-compatible transport manually. The React
   * Native wrapper already injects a transport automatically.
   */
  eventSourceFactory?: EventSourceFactory;

  /**
   * Toolbar configuration
   */
  toolbar?: ToolbarOptions;

  /**
   * Pre-fetched evaluated state used for the initial flag state.
   * The client fetches opt-in metadata on demand when opt-in flags are requested.
   */
  bootstrappedState?: BootstrappedState;

  /**
   * Pre-fetched flags used for the initial flag state.
   * The client fetches opt-in metadata on demand when opt-in flags are requested.
   * @deprecated Use `bootstrappedState` instead.
   */
  bootstrappedFlags?: RawFlags;

  /**
   * Optional storage adapter used for caching flags and overrides.
   * Useful for React Native (AsyncStorage).
   */
  storage?: StorageAdapter;

  /**
   * Queue settings for tracking updates sent to `/bulk`.
   * Applies to user/company updates, check events, and prompt events.
   * Events are buffered in memory and flushed in the background.
   */
  trackingQueue?: {
    /**
     * Delay in milliseconds before flushing queued events.
     * Lower values send sooner; slightly higher values batch better.
     * Defaults to 200ms.
     */
    flushDelayMs?: number;

    /**
     * Maximum number of queued events retained locally.
     * Oldest events are dropped when the cap is exceeded.
     * Defaults to 100.
     */
    maxSize?: number;

    /**
     * Deprecated: retries are no longer performed for bulk delivery.
     */
    retryBaseDelayMs?: number;

    /**
     * Deprecated: retries are no longer performed for bulk delivery.
     */
    retryMaxDelayMs?: number;
  };
};

const defaultConfig: Config = {
  apiBaseUrl: API_BASE_URL,
  appBaseUrl: APP_BASE_URL,
  sseBaseUrl: API_BASE_URL,
  enableTracking: true,
  offline: false,
  bootstrapped: false,
};

const CLIENT_NOT_INITIALIZED_EVALUATION_ERROR = {
  code: "CLIENT_NOT_INITIALIZED",
  field: "",
  message:
    "ReflagClient was not initialized before this flag was evaluated. Call initialize() before evaluating flags.",
} as const;

function withClientInitializationDiagnostic(
  errors: CheckEvent["evaluationErrors"],
  evaluatedBeforeInitialization: boolean,
): CheckEvent["evaluationErrors"] {
  return evaluatedBeforeInitialization
    ? [...(errors ?? []), CLIENT_NOT_INITIALIZED_EVALUATION_ERROR]
    : errors;
}

/**
 * A remotely managed configuration value for a flag.
 */
export type FlagRemoteConfig =
  | {
      /**
       * The key of the matched configuration value.
       */
      key: string;

      /**
       * The optional user-supplied payload data.
       */
      payload: any;
    }
  | { key: undefined; payload: undefined };

/**
 * Options for changing the current user or company's opt-in membership.
 */
export type SetOptInOptions = {
  /**
   * Whether the scoped subject has opted in.
   */
  optedIn: boolean;

  /**
   * Whether to update the current user or current company. Defaults to `user`.
   */
  scope?: "user" | "company";
};

export interface Flag {
  /**
   * Result of flag flag evaluation.
   * Note: Does not take local overrides into account.
   */
  isEnabled: boolean;

  /*
   * Optional user-defined configuration.
   */
  config: FlagRemoteConfig;

  /**
   * Function to send analytics events for this flag.
   */
  track: () => Promise<Response | undefined>;

  /**
   * Function to request feedback for this flag.
   */
  requestFeedback: (
    options: Omit<RequestFeedbackData, "flagKey" | "featureId">,
  ) => void;

  /**
   * The current override status of isEnabled for the flag.
   */
  isEnabledOverride: boolean | null;

  /**
   * Set the override status for isEnabled for the flag.
   * Set to `null` to remove the override.
   */
  setIsEnabledOverride(isEnabled: boolean | null): void;
}

function shouldShowToolbar(opts: InitOptions) {
  const toolbarOpts = opts.toolbar;
  if (typeof window === "undefined" || typeof window.location === "undefined") {
    return false;
  }
  if (typeof toolbarOpts === "boolean") return toolbarOpts;
  if (typeof toolbarOpts?.show === "boolean") return toolbarOpts.show;
  return window.location.hostname === "localhost";
}

/**
 * ReflagClient lets you interact with the Reflag API.
 */
export class ReflagClient {
  private state: State = "idle";
  private initializationFinished = false;
  private contextUpdateLoading = false;
  private readonly publishableKey: string;
  private context: ReflagContext;
  private config: Config;
  private requestFeedbackOptions: Partial<RequestFeedbackOptions>;
  private readonly httpClient: HttpClient;

  private readonly autoFeedback: AutoFeedback | undefined;
  private autoFeedbackInit: Promise<void> | undefined;
  private readonly enableLiveFlagUpdates: boolean;
  private readonly eventSourceFactory: EventSourceFactory | undefined;
  private readonly credentials: RequestCredentials | undefined;
  private readonly sdkVersion: string;
  private pubSubChannel: AblySSEChannel | undefined;
  private pubSubInit: Promise<void> | undefined;
  private latestFlagStateVersionSeen: number | undefined;
  private readonly flagsClient: FlagsClient;
  private readonly bulkQueue: BulkQueue | undefined;
  private readonly handleBeforeUnload?: () => void;

  public readonly logger: Logger;

  private readonly hooks: HooksManager;

  private toolbarToggleShown = false;

  /**
   * Create a new ReflagClient instance.
   */
  constructor(opts: InitOptions) {
    this.publishableKey = opts.publishableKey;
    this.logger =
      opts?.logger ?? loggerWithPrefix(quietConsoleLogger, "[Reflag]");

    // Create the context object making sure to clone the user and company objects
    this.context = normalizeContext(
      opts?.bootstrappedState?.context ?? {
        user: opts?.user,
        company: opts?.company,
        otherContext: opts?.otherContext,
        other: opts?.other,
      },
    );

    const apiBaseUrl = opts?.apiBaseUrl ?? defaultConfig.apiBaseUrl;

    this.config = {
      apiBaseUrl,
      appBaseUrl: opts?.appBaseUrl ?? defaultConfig.appBaseUrl,
      sseBaseUrl: opts?.sseBaseUrl ?? apiBaseUrl,
      enableTracking: opts?.enableTracking ?? defaultConfig.enableTracking,
      offline: opts?.offline ?? defaultConfig.offline,
      bootstrapped:
        !!opts?.bootstrappedState ||
        (opts && "bootstrappedFlags" in opts && !!opts.bootstrappedFlags),
    };

    const requestedEnableLiveFlagUpdates = opts?.enableLiveFlagUpdates ?? false;
    const hasBootstrappedFlagStateVersion = isValidFlagStateVersion(
      opts?.bootstrappedState?.flagStateVersion,
    );
    const bootstrappedWithoutFlagStateVersion =
      (!!opts?.bootstrappedState && !hasBootstrappedFlagStateVersion) ||
      (!!opts && "bootstrappedFlags" in opts && !!opts.bootstrappedFlags);

    if (requestedEnableLiveFlagUpdates && bootstrappedWithoutFlagStateVersion) {
      this.logger.warn(
        "Live flag updates require `flagStateVersion` when bootstrapping flags. Disabling live flag updates for this client. Upgrade your server SDK (for example `@reflag/node-sdk`) so the bootstrap payload includes `flagStateVersion`, or set `enableLiveFlagUpdates` to `false` when using unversioned bootstrap data.",
      );
    }

    this.enableLiveFlagUpdates =
      requestedEnableLiveFlagUpdates && !bootstrappedWithoutFlagStateVersion;
    this.eventSourceFactory = opts?.eventSourceFactory;
    this.credentials = opts?.credentials;
    this.sdkVersion = opts?.sdkVersion ?? SDK_VERSION;

    this.requestFeedbackOptions = {
      position: opts?.feedback?.ui?.position,
      translations: opts?.feedback?.ui?.translations,
    };

    this.httpClient = new HttpClient(this.publishableKey, {
      baseUrl: this.config.apiBaseUrl,
      sdkVersion: this.sdkVersion,
      credentials: opts?.credentials,
    });
    if (!this.config.offline && this.config.enableTracking) {
      this.bulkQueue = new BulkQueue(
        (events) =>
          this.httpClient.post({
            path: "/bulk",
            body: events,
            keepalive: true,
          }),
        {
          flushDelayMs: opts.trackingQueue?.flushDelayMs,
          maxSize: opts.trackingQueue?.maxSize,
          logger: this.logger,
        },
      );
    }
    if (this.bulkQueue && !IS_SERVER) {
      this.handleBeforeUnload = () => {
        void this.bulkQueue?.flush();
      };
      window.addEventListener("beforeunload", this.handleBeforeUnload, {
        capture: true,
      });
    }

    const bulkQueue = this.bulkQueue;

    this.flagsClient = new FlagsClient(
      this.httpClient,
      this.context,
      this.logger,
      {
        bootstrappedState: opts.bootstrappedState
          ? {
              flags: opts.bootstrappedState.flags,
              flagStateVersion: opts.bootstrappedState.flagStateVersion,
            }
          : opts.bootstrappedFlags
            ? { flags: opts.bootstrappedFlags }
            : undefined,
        bootstrappedFlags: opts.bootstrappedFlags,
        expireTimeMs: opts.expireTimeMs,
        staleTimeMs: opts.staleTimeMs,
        staleWhileRevalidate: opts.staleWhileRevalidate,
        timeoutMs: opts.timeoutMs,
        fallbackFlags: opts.fallbackFlags,
        offline: this.config.offline,
        storage: opts.storage,
        enqueueBulkEvent: bulkQueue
          ? (event) => bulkQueue.enqueue(event)
          : undefined,
      },
    );

    if (
      !this.config.offline &&
      this.context?.user &&
      !isNode && // do not prompt on server-side
      opts?.feedback?.enableAutoFeedback !== false // default to on
    ) {
      if (isMobile) {
        this.logger.warn(
          "Feedback prompting is not supported on mobile devices",
        );
      } else {
        this.autoFeedback = new AutoFeedback(
          this.config.sseBaseUrl,
          this.logger,
          this.httpClient,
          opts?.feedback?.autoFeedbackHandler,
          String(this.context.user?.id),
          opts?.feedback?.ui?.position,
          opts?.feedback?.ui?.translations,
          bulkQueue ? (event) => bulkQueue.enqueue(event) : undefined,
          this.credentials,
        );
      }
    }

    if (shouldShowToolbar(opts)) {
      const position =
        typeof opts.toolbar === "object" ? opts.toolbar.position : undefined;
      this.showToolbarToggle(position);
    }

    // Register hooks
    this.hooks = new HooksManager();
    this.flagsClient.onUpdated(() => {
      this.hooks.trigger("flagsUpdated", this.flagsClient.getFlags());
    });
    this.flagsClient.onOptInFlagsLoadingUpdated((isLoading) => {
      this.hooks.trigger("optInFlagsLoadingUpdated", isLoading);
    });
  }

  /**
   * Initialize the Reflag SDK.
   *
   * Must be called before calling other SDK methods.
   */
  async initialize() {
    if (this.state === "initializing" || this.state === "initialized") {
      this.logger.warn(`"Reflag client already ${this.state}`);
      return;
    }
    this.setState("initializing");

    const start = Date.now();
    const wantsPubSub = this.wantsPubSub();
    const hasPubSubTransport = this.hasPubSubTransport();

    if (!this.config.offline && wantsPubSub && !hasPubSubTransport) {
      this.logger.debug(
        "skipping pubsub initialization because this runtime has no browser EventSource transport",
      );
    }

    await this.flagsClient.initialize();

    // Open SSE after the initial flag load. The pubsub server replays the
    // latest flag-update message, including `flagStateVersion`, so
    // `reconcileFlagsWithLatestPubSubVersion()` below can catch up if an update
    // lands between the fetch and the SSE subscription.
    if (!this.config.offline && wantsPubSub && hasPubSubTransport) {
      // do not block on pubsub initialization
      this.logger.debug("initializing pubsub");
      this.pubSubInit = this.initializePubSub().catch((e) => {
        this.logger.error("error initializing pubsub", e);
      });
    }
    this.reconcileFlagsWithLatestPubSubVersion();

    if (!this.config.bootstrapped) {
      if (this.context.user && this.config.enableTracking) {
        this.user().catch((e) => {
          this.logger.error("error sending user", e);
        });
      }

      if (this.context.company && this.config.enableTracking) {
        this.company().catch((e) => {
          this.logger.error("error sending company", e);
        });
      }
    }

    this.logger.info(
      "Reflag initialized in " +
        Math.round(Date.now() - start) +
        "ms" +
        (this.config.offline ? " (offline mode)" : ""),
    );
    this.initializationFinished = true;
    this.setState("initialized");
  }

  /**
   * Stop the SDK.
   * This will stop any automated feedback surveys.
   *
   **/
  async stop() {
    if (this.handleBeforeUnload && !IS_SERVER) {
      window.removeEventListener("beforeunload", this.handleBeforeUnload, {
        capture: true,
      });
    }

    if (this.bulkQueue) {
      await this.bulkQueue.flush();
      let remaining = await this.bulkQueue.size();
      if (remaining > 0) {
        await this.bulkQueue.flush();
        remaining = await this.bulkQueue.size();
      }
      if (remaining > 0) {
        throw new Error(
          `failed to flush all queued bulk events during stop (${remaining} remaining)`,
        );
      }
    }

    if (this.autoFeedback && this.autoFeedbackInit) {
      // ensure fully initialized before stopping
      await this.autoFeedbackInit;
      this.autoFeedback.stop();
    }

    if (this.pubSubInit) {
      // ensure fully initialized before stopping
      await this.pubSubInit;
    }
    if (this.pubSubChannel) {
      this.pubSubChannel.close();
      this.pubSubChannel = undefined;
    }

    this.flagsClient.stop();
    this.setState("stopped");
  }

  getState() {
    return this.state;
  }

  /**
   * Add an event listener
   *
   * @param type Type of events to listen for
   * @param handler The function to call when the event is triggered.
   * @returns A function to remove the hook.
   */
  on<THookType extends keyof HookArgs>(
    type: THookType,
    handler: (args0: HookArgs[THookType]) => void,
  ) {
    return this.hooks.addHook(type, handler);
  }

  /**
   * Remove an event listener
   *
   * @param type Type of event to remove.
   * @param handler The same function that was passed to `on`.
   *
   * @returns A function to remove the hook.
   */
  off<THookType extends keyof HookArgs>(
    type: THookType,
    handler: (args0: HookArgs[THookType]) => void,
  ) {
    this.hooks.removeHook(type, handler);
  }

  /**
   * Get the current context.
   */
  getContext() {
    return this.context;
  }

  /**
   * Get the current configuration.
   */
  getConfig() {
    return this.config;
  }

  /**
   * Update the user context.
   * Performs a shallow merge with the existing user context.
   * It will not update the context if nothing has changed.
   *
   * @param user
   */
  async updateUser(user: { [key: string]: string | number | undefined }) {
    const newUserContext = {
      ...this.context.user,
      ...user,
      id: user.id ?? this.context.user?.id,
    };

    return this.applyContext(
      {
        user: newUserContext,
        company: this.context.company,
        other: this.context.other,
      },
      { warnOnMissingIds: false },
    );
  }

  /**
   * Update the company context.
   * Performs a shallow merge with the existing company context.
   * It will not update the context if nothing has changed.
   *
   * @param company The company details.
   */
  async updateCompany(company: { [key: string]: string | number | undefined }) {
    const newCompanyContext = {
      ...this.context.company,
      ...company,
      id: company.id ?? this.context.company?.id,
    };

    return this.applyContext(
      {
        user: this.context.user,
        company: newCompanyContext,
        other: this.context.other,
      },
      { warnOnMissingIds: false },
    );
  }

  /**
   * Update the company context.
   * Performs a shallow merge with the existing company context.
   * It will not update the context if nothing has changed.
   *
   * @param otherContext Additional context.
   */
  async updateOtherContext(otherContext: {
    [key: string]: string | number | undefined;
  }) {
    const newOtherContext = {
      ...this.context.other,
      ...otherContext,
    };

    return this.applyContext(
      {
        user: this.context.user,
        company: this.context.company,
        other: newOtherContext,
      },
      { warnOnMissingIds: false },
    );
  }

  /**
   * Update the context.
   * Replaces the existing context with a new context.
   *
   * @param context The context to update.
   */
  async setContext(context: ReflagDeprecatedContext) {
    return this.applyContext(context, { warnOnMissingIds: true });
  }

  private async applyContext(
    context: ReflagDeprecatedContext,
    { warnOnMissingIds }: { warnOnMissingIds: boolean },
  ) {
    const previousContext = this.context;
    const newContext = normalizeContext(context);

    if (warnOnMissingIds && !context.user?.id) {
      this.logger.warn("No user Id provided in context, user will be ignored");
    }
    if (warnOnMissingIds && !context.company?.id) {
      this.logger.warn(
        "No company Id provided in context, company will be ignored",
      );
    }

    // Nothing has changed, skipping update
    if (deepEqual(previousContext, newContext)) return;

    const userChanged = !deepEqual(previousContext.user, newContext.user);
    const companyChanged = !deepEqual(
      previousContext.company,
      newContext.company,
    );
    const userIdChanged =
      !!newContext.user?.id && newContext.user.id !== previousContext.user?.id;

    this.context = newContext;

    let shouldFlushIdentityUpdate = false;

    if (companyChanged) {
      void this.company();
      shouldFlushIdentityUpdate = true;
    }

    if (userChanged) {
      void this.user();
      shouldFlushIdentityUpdate = true;
      // Update the automatic feedback user if the user ID has changed
      if (userIdChanged) {
        void this.updateAutoFeedbackUser(String(newContext.user!.id));
      }
    }

    if (shouldFlushIdentityUpdate) {
      void this.bulkQueue?.flush();
    }

    const shouldTrackLoading = this.state === "initialized";
    if (shouldTrackLoading) {
      this.contextUpdateLoading = true;
      this.setState("initializing");
    }

    const didApply = await this.flagsClient.setContext(this.context);
    if (didApply) {
      this.finishContextUpdate();
    }
  }

  applyBootstrappedState(
    bootstrappedState: BootstrappedState,
    triggerEvent = true,
  ) {
    const previousContext = this.context;
    const newContext = normalizeContext(bootstrappedState.context);

    const contextChanged = !deepEqual(previousContext, newContext);
    const userChanged = !deepEqual(previousContext.user, newContext.user);
    const companyChanged = !deepEqual(
      previousContext.company,
      newContext.company,
    );
    const userIdChanged =
      !!newContext.user?.id && newContext.user.id !== previousContext.user?.id;

    const currentFlagStateVersion = this.flagsClient.getFlagStateVersion();
    const latestKnownFlagStateVersion = Math.max(
      currentFlagStateVersion ?? -1,
      this.latestFlagStateVersionSeen ?? -1,
    );
    if (latestKnownFlagStateVersion >= 0) {
      this.latestFlagStateVersionSeen = latestKnownFlagStateVersion;
    }

    const incomingFlagStateVersion = bootstrappedState.flagStateVersion;
    if (isValidFlagStateVersion(incomingFlagStateVersion)) {
      this.latestFlagStateVersionSeen = Math.max(
        this.latestFlagStateVersionSeen ?? -1,
        incomingFlagStateVersion,
      );
    }

    const shouldIgnoreIncomingFlags =
      !contextChanged &&
      latestKnownFlagStateVersion >= 0 &&
      (incomingFlagStateVersion === undefined ||
        incomingFlagStateVersion < latestKnownFlagStateVersion);

    this.context = newContext;
    this.flagsClient.setContextWithoutFetch(
      newContext,
      !shouldIgnoreIncomingFlags,
    );

    if (!shouldIgnoreIncomingFlags) {
      this.flagsClient.resetOptInMetadataRefresh();
      this.flagsClient.setFetchedFlags(
        bootstrappedState.flags,
        triggerEvent,
        incomingFlagStateVersion,
      );
      this.flagsClient.markBootstrappedStateApplied();
    }

    if (contextChanged) {
      this.finishContextUpdate();
    }

    if (!contextChanged) {
      return;
    }

    if (companyChanged) {
      void this.company();
    }

    if (userChanged) {
      void this.user();
      if (userIdChanged) {
        void this.updateAutoFeedbackUser(String(newContext.user!.id));
      }
    }

    this.reconcileFlagsWithLatestPubSubVersion();
  }

  /**
   * Update the flags.
   *
   * @param flags The flags to update.
   * @param triggerEvent Whether to trigger the `flagsUpdated` event.
   */
  updateFlags(flags: RawFlags, triggerEvent = true, flagStateVersion?: number) {
    this.flagsClient.setFetchedFlags(flags, triggerEvent, flagStateVersion);
  }

  /**
   * Track an event in Reflag.
   *
   * @param eventName The name of the event.
   * @param attributes Any attributes you want to attach to the event.
   */
  async track(
    eventName: string,
    attributes?: Record<string, any> | null,
  ): Promise<Response | undefined> {
    if (!this.context.user) {
      this.logger.warn("'track' call ignored. No user context provided");
      return;
    }
    if (!this.config.enableTracking) {
      this.logger.warn("'track' call ignored. 'enableTracking' is false");
      return;
    }

    if (this.config.offline) {
      return;
    }

    const payload: TrackedEvent = {
      userId: String(this.context.user.id),
      event: eventName,
    };
    if (attributes) payload.attributes = attributes;
    if (this.context.company?.id)
      payload.companyId = String(this.context.company?.id);

    const res = await this.httpClient.post({ path: "/event", body: payload });
    if (!res.ok) {
      await logResponseError({
        logger: this.logger,
        res,
        message: "track request failed",
        extra: { event: eventName },
      });
    }
    this.logger.debug(`sent event`, payload);

    this.hooks.trigger("track", {
      eventName,
      attributes,
      user: this.context.user,
      company: this.context.company,
    });
    return res;
  }

  /**
   * Submit user feedback to Reflag. Must include either `score` or `comment`, or both.
   *
   * @param payload The feedback details to submit.
   * @returns The server response.
   */
  async feedback(payload: Feedback) {
    if (this.config.offline) {
      return;
    }

    const userId =
      payload.userId ||
      (this.context.user?.id ? String(this.context.user?.id) : undefined);

    const companyId =
      payload.companyId ||
      (this.context.company?.id ? String(this.context.company?.id) : undefined);

    return await feedback(this.httpClient, this.logger, {
      userId,
      companyId,
      ...payload,
    });
  }

  /**
   * Display the Reflag feedback form UI programmatically.
   *
   * This can be used to collect feedback from users in Reflag in cases where Automated Feedback Surveys isn't appropriate.
   *
   * @param options
   */
  requestFeedback(options: RequestFeedbackData) {
    if (!this.context.user?.id) {
      this.logger.error(
        "`requestFeedback` call ignored. No `user` context provided at initialization",
      );
      return;
    }

    if (!options.flagKey) {
      this.logger.error(
        "`requestFeedback` call ignored. No `flagKey` provided",
      );
      return;
    }

    const feedbackData = {
      flagKey: options.flagKey,
      companyId:
        options.companyId ||
        (this.context.company?.id
          ? String(this.context.company?.id)
          : undefined),
      source: "widget" as const,
    } satisfies Feedback;

    // Wait a tick before opening the feedback form,
    // to prevent the same click from closing it.
    setTimeout(() => {
      feedbackLib.openFeedbackForm({
        key: options.flagKey,
        title: options.title,
        position: options.position || this.requestFeedbackOptions.position,
        translations:
          options.translations || this.requestFeedbackOptions.translations,
        openWithCommentVisible: options.openWithCommentVisible,
        onClose: options.onClose,
        onDismiss: options.onDismiss,
        onScoreSubmit: async (data) => {
          const res = await this.feedback({
            ...feedbackData,
            ...data,
          });

          if (res) {
            const json = await res.json();
            return { feedbackId: json.feedbackId };
          }
          return { feedbackId: undefined };
        },
        onSubmit: async (data) => {
          // Default onSubmit handler
          await this.feedback({
            ...feedbackData,
            ...data,
          });

          options.onAfterSubmit?.(data);
        },
      });
    }, 1);
  }

  /**
   * @deprecated Use `getFlags` instead.
   */
  getFeatures() {
    return this.getFlags();
  }

  /**
   * Returns a map of enabled flags.
   * Accessing a flag will *not* send a check event
   * and `isEnabled` does not take any flag overrides
   * into account.
   *
   * @returns Map of flags.
   */
  getFlags(): RawFlags {
    return this.flagsClient.getFlags();
  }

  /**
   * Force refresh flags from the API, bypassing cache.
   */
  refresh() {
    return this.flagsClient.refreshFlags();
  }

  /**
   * Returns opt-in-enabled flags for the current context.
   */
  getOptInFlags(): OptInFlag[] {
    this.flagsClient.requestOptInFlags();

    return Object.values(this.getFlags()).flatMap((flag) => {
      if (flag.optInEnabled !== true || !flag.optIn) return [];

      return {
        key: flag.key,
        name: flag.optIn.name,
        description: flag.optIn.description,
        isEnabled: flag.isEnabledOverride ?? flag.isEnabled,
        userOptedIn: flag.optIn.userOptedIn,
        companyOptedIn: flag.optIn.companyOptedIn,
        isOptedIn: flag.optIn.isOptedIn,
      } satisfies OptInFlag;
    });
  }

  /**
   * Returns whether opt-in flags are loading for the current context.
   *
   * Calling this method requests opt-in metadata if it is not already available.
   */
  getIsLoadingOptInFlags(): boolean {
    this.flagsClient.requestOptInFlags();
    return this.flagsClient.getIsLoadingOptInFlags();
  }

  /**
   * Set whether the current user or company has opted into a flag.
   *
   * A successful Response is returned after the refreshed flag state confirms
   * the membership change. HTTP failures return a non-OK Response without
   * refreshing flags. Offline mode, invalid arguments, or missing scoped context
   * return undefined. Network and confirmation failures reject the promise;
   * a confirmation failure may occur after membership changed remotely.
   */
  async setOptIn(
    flagKey: string,
    options: SetOptInOptions,
  ): Promise<Response | undefined> {
    if (this.config.offline) {
      return;
    }

    if (typeof flagKey !== "string" || !flagKey) {
      this.logger.error("`setOptIn` call ignored. No `flagKey` provided");
      return;
    }

    if (!options || typeof options.optedIn !== "boolean") {
      this.logger.error("`setOptIn` call ignored. `optedIn` must be a boolean");
      return;
    }

    const scope = options.scope ?? "user";
    if (scope !== "user" && scope !== "company") {
      this.logger.error(
        '`setOptIn` call ignored. `scope` must be "user" or "company"',
      );
      return;
    }

    const scopedContext = this.context[scope];
    if (!scopedContext?.id) {
      this.logger.error(
        `\`setOptIn\` call ignored. No \`${scope}\` context provided`,
      );
      return;
    }

    const failConfirmation = (message: string): never => {
      const error = new Error(message);
      this.logger.error("set opt-in confirmation failed", error);
      throw error;
    };

    const scopedContextId = String(scopedContext.id);
    const assertScopedContextUnchanged = () => {
      const currentScopedContext = this.context[scope];
      if (
        currentScopedContext?.id &&
        String(currentScopedContext.id) === scopedContextId
      ) {
        return;
      }

      failConfirmation(
        `Opt-in changed remotely, but the ${scope} context changed before the updated state could be confirmed`,
      );
    };

    const context = {
      user: this.context.user
        ? { ...this.context.user, id: String(this.context.user.id) }
        : undefined,
      company: this.context.company
        ? { ...this.context.company, id: String(this.context.company.id) }
        : undefined,
      other: this.context.other,
    };

    const res = await this.httpClient.post({
      path: "/flags/opt-in",
      body: {
        key: flagKey,
        optedIn: options.optedIn,
        scope,
        context,
      },
    });

    if (!res.ok) {
      await logResponseError({
        logger: this.logger,
        res: res.clone(),
        message: "set opt-in request failed",
        extra: { flagKey, optedIn: options.optedIn, scope },
      });
      return res;
    }

    let flagStateVersion: number;
    try {
      const body = await res.clone().json();
      if (!isValidFlagStateVersion(body?.flagStateVersion)) {
        throw new Error("Response did not include a valid flag state version");
      }
      flagStateVersion = body.flagStateVersion;
    } catch (error) {
      this.logger.error(
        "set opt-in succeeded but its flag state version could not be read",
        error,
      );
      throw error;
    }

    assertScopedContextUnchanged();
    const refreshedFlags =
      await this.flagsClient.refreshFlags(flagStateVersion);
    assertScopedContextUnchanged();
    if (!refreshedFlags) {
      failConfirmation(
        "Opt-in changed remotely, but the updated flag state could not be confirmed",
      );
    }

    const refreshedOptIn = this.flagsClient.getFetchedFlags()[flagKey]?.optIn;
    const scopedOptIn =
      scope === "user"
        ? refreshedOptIn?.userOptedIn
        : refreshedOptIn?.companyOptedIn;
    const isConfirmed = options.optedIn
      ? scopedOptIn === true
      : scopedOptIn !== true;
    if (!isConfirmed) {
      failConfirmation(
        `Opt-in changed remotely, but the updated ${scope} membership was not reflected in the SDK`,
      );
    }

    return res;
  }

  /**
   * @deprecated Use `getFlag` instead.
   */
  getFeature(flagKey: string) {
    return this.getFlag(flagKey);
  }

  /**
   * Return a flag. Accessing `isEnabled` or `config` will automatically send a `check` event.
   *
   * @param flagKey - The key of the flag to get.
   * @returns A flag.
   */
  getFlag(flagKey: string): Flag {
    const f = this.getFlags()[flagKey];
    const evaluatedBeforeInitialization =
      !this.initializationFinished &&
      !this.config.offline &&
      !this.config.bootstrapped;

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    const value = f?.isEnabledOverride ?? f?.isEnabled ?? false;
    const config = f?.config
      ? {
          key: f.config.key,
          payload: f.config.payload,
        }
      : { key: undefined, payload: undefined };

    return {
      get isEnabled() {
        self
          .sendCheckEvent({
            action: "check-is-enabled",
            key: flagKey,
            version: f?.targetingVersion,
            ruleEvaluationResults: f?.ruleEvaluationResults,
            missingContextFields: f?.missingContextFields,
            evaluationErrors: withClientInitializationDiagnostic(
              f?.evaluationErrors,
              evaluatedBeforeInitialization,
            ),
            value,
          })
          .catch(() => {
            // ignore
          });
        return value;
      },
      get config() {
        self
          .sendCheckEvent({
            action: "check-config",
            key: flagKey,
            version: f?.config?.version,
            ruleEvaluationResults: f?.config?.ruleEvaluationResults,
            missingContextFields: f?.config?.missingContextFields,
            evaluationErrors: withClientInitializationDiagnostic(
              f?.config?.evaluationErrors,
              evaluatedBeforeInitialization,
            ),
            value: f?.config && {
              key: f.config.key,
              payload: f.config.payload,
            },
          })
          .catch(() => {
            // ignore
          });

        return config;
      },
      track: () => this.track(flagKey),
      requestFeedback: (
        options: Omit<RequestFeedbackData, "flagKey" | "featureId">,
      ) => {
        this.requestFeedback({
          flagKey,
          ...options,
        });
      },
      isEnabledOverride: this.flagsClient.getFlagOverride(flagKey),
      setIsEnabledOverride(isEnabled: boolean | null) {
        self.flagsClient.setFlagOverride(flagKey, isEnabled);
      },
    };
  }

  showToolbarToggle(position?: ToolbarPosition) {
    if (this.toolbarToggleShown) {
      return;
    }
    this.toolbarToggleShown = true;
    this.logger.info("opening toolbar toggler");

    showToolbarToggle({
      reflagClient: this,
      position,
    });
  }

  private finishContextUpdate() {
    if (!this.contextUpdateLoading) return;

    this.contextUpdateLoading = false;
    if (this.state === "initializing") {
      this.setState("initialized");
    }
  }

  private setState(state: State) {
    this.state = state;
    this.hooks.trigger("stateUpdated", state);
  }

  private sendCheckEvent(checkEvent: CheckEvent) {
    return this.flagsClient.sendCheckEvent(checkEvent, () => {
      this.hooks.trigger("check", checkEvent);
    });
  }

  /**
   * Send attributes to Reflag for the current user
   */
  private async user() {
    if (!this.context.user) {
      this.logger.warn(
        "`user` call ignored. No user context provided at initialization",
      );
      return;
    }

    if (this.config.offline) {
      return;
    }
    if (!this.config.enableTracking) {
      return;
    }
    if (!this.bulkQueue) {
      return;
    }

    const { id, ...attributes } = this.context.user;
    const payload: BulkEvent = {
      type: "user",
      userId: String(id),
      attributes,
    };
    await this.bulkQueue.enqueue(payload);
    this.logger.debug(`queued user`, payload);

    this.hooks.trigger("user", this.context.user);
    return;
  }

  /**
   * Send attributes to Reflag for the current company.
   */
  private async company() {
    if (!this.context.user) {
      this.logger.warn(
        "`company` call ignored. No user context provided at initialization",
      );
      return;
    }

    if (!this.context.company) {
      this.logger.warn(
        "`company` call ignored. No company context provided at initialization",
      );
      return;
    }

    if (this.config.offline) {
      return;
    }
    if (!this.config.enableTracking) {
      return;
    }
    if (!this.bulkQueue) {
      return;
    }

    const { id, ...attributes } = this.context.company;
    const payload: BulkEvent = {
      type: "company",
      userId: String(this.context.user.id),
      companyId: String(id),
      attributes,
    };
    await this.bulkQueue.enqueue(payload);
    this.logger.debug(`queued company`, payload);
    this.hooks.trigger("company", this.context.company);
    return;
  }

  private wantsPubSub() {
    return this.enableLiveFlagUpdates || !!this.autoFeedback;
  }

  private hasPubSubTransport() {
    return !IS_SERVER || !!this.eventSourceFactory;
  }

  private async initializePubSub() {
    this.pubSubChannel?.close();
    this.pubSubChannel = undefined;

    this.pubSubChannel = openAblySSEChannel({
      channels: [],
      callback: (message) => this.handlePubSubMessage(message),
      logger: this.logger,
      sseBaseUrl: this.config.sseBaseUrl,
      eventSourceFactory: this.eventSourceFactory,
      publishableKey: this.publishableKey,
      sdkVersion: this.sdkVersion,
      path: "sse/client",
      context: this.context,
      credentials: this.credentials,
    });
  }

  private handlePubSubMessage(message: PubSubMessage) {
    const eventName =
      typeof message.name === "string"
        ? message.name
        : typeof message.event === "string"
          ? message.event
          : undefined;
    const payload = message.data ?? message;

    const flagStateVersion = Number((payload as any)?.flagStateVersion);
    const hasFlagStateVersion = isValidFlagStateVersion(flagStateVersion);

    if (eventName === "flags-updated" || hasFlagStateVersion) {
      if (!this.enableLiveFlagUpdates) {
        return;
      }

      if (hasFlagStateVersion) {
        this.latestFlagStateVersionSeen = Math.max(
          this.latestFlagStateVersionSeen ?? -1,
          flagStateVersion,
        );
      }

      const currentVersion = this.flagsClient.getFlagStateVersion();
      if (
        this.state === "initialized" &&
        (!hasFlagStateVersion ||
          currentVersion === undefined ||
          flagStateVersion > currentVersion)
      ) {
        void this.flagsClient
          .refreshFlags(hasFlagStateVersion ? flagStateVersion : undefined)
          .catch((e) => {
            this.logger.error("error refreshing flags after live update", e);
          });
      }
      return;
    }

    if (this.autoFeedback && this.context.user?.id) {
      this.autoFeedback.handleFeedbackPromptRequest(
        String(this.context.user.id),
        payload,
      );
    }
  }

  private reconcileFlagsWithLatestPubSubVersion() {
    if (!this.enableLiveFlagUpdates) {
      return;
    }

    const latestVersion = this.latestFlagStateVersionSeen;
    const currentVersion = this.flagsClient.getFlagStateVersion();

    if (
      latestVersion !== undefined &&
      (currentVersion === undefined || latestVersion > currentVersion)
    ) {
      void this.flagsClient.refreshFlags(latestVersion).catch((e) => {
        this.logger.error(
          "error refreshing flags after pubsub version reconciliation",
          e,
        );
      });
    }
  }

  private async updateAutoFeedbackUser(userId: string) {
    if (!this.autoFeedback) {
      return;
    }

    if (this.pubSubInit) {
      await this.pubSubInit;
      this.autoFeedback.setUserId(userId);
      this.pubSubInit = this.initializePubSub().catch((e) => {
        this.logger.error("error reinitializing pubsub", e);
      });
      await this.pubSubInit;
      return;
    }

    // Ensure fully initialized before updating the user
    await this.autoFeedbackInit;
    await this.autoFeedback.setUser(userId);
  }
}
